<template>
  <div v-if="showLogout">
    <TransitionRoot as="template" :show="showLogout">
      <DialogWidget as="div" class="fixed z-20 inset-0 overflow-y-auto" @close="showLogout = false">
        <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
            <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <!-- This element is to trick the browser into centering the modal contents. -->
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <div class="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div class="sm:flex sm:items-start">
                <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationIcon class="h-6 w-6 text-red-600" aria-hidden="true" />
                </div>
                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900"> {{ $t('sidebar.logoutQuestion') }}</DialogTitle>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">
                      {{ $t('sidebar.logoutMessage') }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button type="button" class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" @click="logout">{{ $t('actions.logout') }}</button>
                <button type="button" class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm" @click="showLogout = false" ref="cancelButtonRef">{{ $t('actions.cancel') }}</button>
              </div>
            </div>
          </TransitionChild>
        </div>
      </DialogWidget>
    </TransitionRoot>
  </div>
  <div v-else>
    <div class="fixed z-20 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 class="text-lg leading-6 font-medium text-gray-900 pb-5 border-b border-gray-300" id="modal-title">
                  {{ agent.attributes.name }}
                </h3>
                <div class="mt-5 mb-5">
                  <LocaleSelector v-on:locale-set="setLocale" />
                </div>
              </div>
            </div>
            <div class="sm:flex sm:items-start">
              <div class="text-center sm:mt-0 sm:text-left w-full">
                <div class="mb-5 text-sm font-medium text-gray-700">
                  {{ $t('settings.notifications') }}:
                  <fieldset class="mt-4">
                    <div class="space-y-4">
                      <div v-for="level in notificationLevels" :key="level.id" class="flex items-center">
                        <input :id="level.id" :value="level.id" v-model="settings.attributes['notifications-level']" name="notification-method" type="radio" :checked="level.id === settings.attributes['notifications-level']" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
                        <label :for="level.id" class="ml-3 block text-sm font-normal text-gray-700">
                          {{ level.text }}
                        </label>
                      </div>
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
            <div class="sm:flex sm:items-start">
              <div class="text-center sm:mt-0 sm:text-left w-full">
                <div class="mb-5 text-sm font-medium text-gray-700">
                  {{ $t('settings.browserNotifications') }}:
                  <p class="mt-2 block text-sm font-normal text-gray-700">
                    {{notificationPermissionAsText}}
                    <span v-if="notificationPermission === 'default'" @click="askForPermissions" class="text-indigo-600 cursor-pointer">[{{ $t('settings.ask') }}]</span>
                  </p>
                  <div v-if="notificationPermission === 'denied'" class="text-red-600 mt-5">{{ $t('settings.notificationsBrowserDisabled') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6">
             <button type="button" v-on:click="askForLogout" class="pr-9 relative inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
               {{ $t('actions.logout') }}
               <LogoutIcon class=" h-5 w-5 right-2 bottom-2 absolute inline-block" />
             </button>
             <button v-on:click="save" type="button" class="bg-indigo-600 float-right py-2 px-4 border border-indigo-600 rounded-md shadow-sm text-sm font-medium text-white">
               {{ $t('actions.save') }}
             </button>
             <button v-on:click="close" type="button" class="mr-3 float-right bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
               {{ $t('actions.close') }}
             </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LocaleSelector from './LocaleSelector.vue'
import { LogoutIcon } from '@heroicons/vue/outline'
import dashboardSettingsModel from '@/models/dashboardSettings'

import { Dialog as DialogWidget, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { ExclamationIcon } from '@heroicons/vue/outline'

export default {
  name: 'AgentProfile',
  props: ['agent'],
  components: {
    LocaleSelector,
    LogoutIcon,
    //
    DialogWidget,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    ExclamationIcon,
  },
  created() {
    this.checkPermissions();
  },
  computed: {
    settings() {
      return this.$root.agentDashboardSettings;
    },
    settingsNotificationLevel() {
      return this.settings.attributes['notifications-level'];
    },
    notificationPermissionAsText() {
      let texts = {
        'default': this.$t('settings.default'),
        'granted': this.$t('settings.granted'),
        'denied': this.$t('settings.denied')
      }
      return texts[this.notificationPermission];
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    logout() {
      this.$emit('logout');
    },
    askForLogout() {
      this.showLogout = true;
    },
    setLocale(locale) {
      this.locale = locale;
    },
    async save() {
      let data = dashboardSettingsModel.buildUpdate(this.locale, this.settings.attributes['notifications-level']);
      await dashboardSettingsModel.updateSettings(this.axios, this.$root.token, data);
      this.$emit('close');
    },
    checkPermissions() {
      this.notificationPermission = window.Notification.permission;
    },
    askForPermissions() {
      this.$notification.requestPermission().then((state) => {
        this.notificationPermission = state;
      });
    },
  },
  watch: {
    settingsNotificationLevel(level) {
      if ((level === 'all' || level === 'muted') && this.notificationPermission !== 'granted') {
        this.askForPermissions();
      }
    }
  },
  data() {
    return {
      showLogout: false,
      locale: undefined,
      notificationPermission: undefined,
      notificationLevels: [
        {id: 'all', text: this.$t('settings.notifyAll')},
        {id: 'muted', text: this.$t('settings.notifyMuted')},
        {id: 'disabled', text: this.$t('settings.notifyDisabled')},
      ]
    }
  },
}
</script>
